import { PaymentMethod } from "../../../../common/enums";
import { isUSA } from "../../../../common/utils";
import { type AddressInputType } from "../../../SharedAddressInput/constants";
import { type DetailedBecsPaymentPrefObjectType } from "../../common/types";
import { type SelectedBankCountry } from "../../Context/types";
import { getIdFromCountry, getNameFromCountry, mapCountryToValueSafely } from "../../mappers";
import { BecsPaymentPrefInputFields, type BecsPaymentPrefInputType } from "./constants";

type BecsAddress = NonNullable<DetailedBecsPaymentPrefObjectType["accountHolderAddress"]>;

export const stripBsbDash = (value: string): string => value.replace(/-/g, "");

export const formatBsbForInput = (value: string): string => {
  const digits = stripBsbDash(value);
  if (digits.length !== 6 || !/^[0-9]+$/.test(digits)) return value;
  return `${digits.slice(0, 3)}-${digits.slice(3)}`;
};

export const mapFormikAddressToGQLObject = (address: AddressInputType): BecsAddress => {
  const isUSACountry = isUSA(getNameFromCountry(address?.country));

  return {
    city: address?.city?.trim() || null,
    country: address?.country
      ? {
          id: getIdFromCountry(address?.country),
          name: getNameFromCountry(address?.country),
          alpha2code: null,
          code: null,
        }
      : null,
    postalCode: address?.zip?.trim() || null,
    region: !isUSACountry ? address?.province?.trim() || null : null,
    state: isUSACountry
      ? {
          code: address?.state?.trim() ?? "",
          id: address?.state?.trim() ?? "",
          name: "",
        }
      : null,
    street: address?.streetLine1?.trim() || null,
    street2: address?.streetLine2?.trim() || null,
    street3: null,
  };
};

export const mapFormStateToContextState = (
  values: BecsPaymentPrefInputType,
  bankCountry?: SelectedBankCountry,
): DetailedBecsPaymentPrefObjectType => {
  const accountName = values[BecsPaymentPrefInputFields.AccountName].trim();
  const bsb = stripBsbDash(values[BecsPaymentPrefInputFields.BsbCode].trim());
  const accountNumber = values[BecsPaymentPrefInputFields.AccountNumber].trim();
  const bankName = values[BecsPaymentPrefInputFields.BankName].trim();

  const accountHolderAddress = mapFormikAddressToGQLObject(values.address);

  return {
    __typename: "BecsPaymentPrefObjectType",
    id: null,
    paymentPrefId: null,
    paymentMethod: PaymentMethod.BECS,
    accountName,
    accountNumber,
    bank: {
      __typename: "BecsBankObjectType" as const,
      bsb,
      name: bankName,
      address: {
        city: null,
        postalCode: null,
        region: null,
        state: null,
        street: null,
        street2: null,
        street3: null,
        country: bankCountry
          ? {
              id: bankCountry.id,
              name: bankCountry.name,
              alpha2code: bankCountry.alpha2code ?? null,
              code: null,
            }
          : null,
      },
    },
    additionalInstructions:
      values[BecsPaymentPrefInputFields.AdditionalInstructions]?.trim() || null,
    accountHolderAddress,
    bankInfoRedacted: `${bankName} account ending in ${accountNumber.slice(-4)}`,
  };
};

const mapGQLAddressToFormikState = (gqlAddress?: BecsAddress | null): AddressInputType => {
  const countryName = gqlAddress?.country?.name;

  return {
    sameAsMailingAddress: false,
    streetLine1: gqlAddress?.street ?? "",
    streetLine2: gqlAddress?.street2 ?? "",
    city: gqlAddress?.city ?? "",
    zip: gqlAddress?.postalCode ?? "",
    state: countryName && isUSA(countryName) ? gqlAddress?.state?.code ?? "" : "",
    province: countryName && !isUSA(countryName) ? gqlAddress?.region ?? "" : "",
    country: mapCountryToValueSafely(gqlAddress?.country?.id, countryName),
  };
};

export const mapContextStateToFormState = (
  input?: DetailedBecsPaymentPrefObjectType,
): BecsPaymentPrefInputType => {
  return {
    accountName: input?.accountName ?? "",
    bsbCode: formatBsbForInput(input?.bank?.bsb ?? ""),
    bankName: input?.bank?.name ?? "",
    accountNumber: input?.accountNumber ?? "",
    confirmAccountNumber: input?.accountNumber ?? "",
    additionalInstructions: input?.additionalInstructions ?? "",
    [BecsPaymentPrefInputFields.BankCountryValidatedCountry]: "",
    address: mapGQLAddressToFormikState(input?.accountHolderAddress),
  };
};
